import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, Gift, Clock, User, History, ChevronRight, CreditCard, X, BellRing, CalendarClock, Pencil, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useAuth, api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { toast } from 'sonner';
// ============================================================
// UTILITÁRIAS DE TIMEZONE (mesmas do SchedulePage)
// ============================================================
const toUTCDate = (isoStringOrDate) => {
  if (!isoStringOrDate) return null;
  if (isoStringOrDate instanceof Date) return isoStringOrDate;
  const s = String(isoStringOrDate).trim();
  // Se já tem timezone explícito, parseia direto
  if (s.endsWith('Z') || s.includes('+') || /\d{2}-\d{2}:\d{2}$/.test(s)) return new Date(s);
  // Se não tem timezone (string "naive"), assume UTC para não depender do timezone do browser
  return new Date(s + 'Z');
};
const formatLisbon = (isoStringOrDate) => {
  const d = toUTCDate(isoStringOrDate);
  if (!d || isNaN(d.getTime())) return '--:--';
  return d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Lisbon' });
};
const formatDateTimeLisbon = (isoStringOrDate) => {
  const d = toUTCDate(isoStringOrDate);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-PT', {
    timeZone: 'Europe/Lisbon',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
};
// ============================================================
// ── LISTA DE ESPERA: área da cliente (fase de pedidos, sem ofertas automáticas) ──
// Lista de espera visível no Dashboard; backend controla ofertas e permissões.
// Não depende de REACT_APP_WAITLIST_UI_ENABLED nem ativa ofertas por si só.
const WAITLIST_UI_ENABLED = true;
const waitlistDatePT = raw => {
  if (!raw) return '—';
  const parts = String(raw).slice(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : raw;
};
const waitlistToday = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Lisbon', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const waitlistMaxDate = () => {
  const d = new Date(`${waitlistToday()}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 120);
  return d.toISOString().slice(0, 10);
};
const WAITLIST_PERIODS = [
  { key: 'any', pt: 'Qualquer horário', en: 'Any time' },
  { key: 'morning', pt: 'Manhã (até às 12h)', en: 'Morning (before 12)' },
  { key: 'afternoon', pt: 'Tarde (12h–17h)', en: 'Afternoon (12–17)' },
  { key: 'evening', pt: 'Fim do dia (a partir das 17h)', en: 'Evening (17h onwards)' },
];
const waitlistStatusStyle = {
  waiting: 'bg-amber-50 text-amber-800 border-amber-200',
  expired: 'bg-gray-100 text-gray-600 border-gray-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  converted: 'bg-green-50 text-green-800 border-green-200',
};
const waitlistStatusLabel = {
  pt: { waiting: 'Em espera', expired: 'Prazo terminado', cancelled: 'Cancelado', converted: 'Convertido em marcação' },
  en: { waiting: 'Waiting', expired: 'Date range ended', cancelled: 'Cancelled', converted: 'Converted to booking' },
};
const waitlistError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) return detail.map(x => x?.msg || '').filter(Boolean).join('; ') || fallback;
  return fallback;
};
const WaitlistEditModal = ({ entry, services, staff, user, language, onClose, onSaved }) => {
  const pt = language === 'pt';
  const [from, setFrom] = useState(entry.date_from < waitlistToday() ? waitlistToday() : entry.date_from);
  const [to, setTo] = useState(entry.date_to < waitlistToday() ? waitlistToday() : entry.date_to);
  const [periods, setPeriods] = useState(entry.periods?.length ? entry.periods : ['any']);
  const [earliest, setEarliest] = useState(entry.earliest_time || '');
  const [latest, setLatest] = useState(entry.latest_time || '');
  const [staffId, setStaffId] = useState(entry.staff_id || 'any');
  const [notes, setNotes] = useState(entry.notes || '');
  const [email, setEmail] = useState(!!entry.email_opt_in);
  const [whatsapp, setWhatsapp] = useState(!!entry.whatsapp_opt_in);
  const [saving, setSaving] = useState(false);
  const compatibleStaff = staff.filter(person => entry.service_ids.every(id => {
    const service = services.find(s => s.service_id === id);
    return service && (!service.staff_ids?.length || service.staff_ids.includes(person.staff_id));
  }));
  const togglePeriod = p => setPeriods(old => {
    if (p === 'any') return ['any'];
    const next = old.includes(p) ? old.filter(x => x !== p) : [...old.filter(x => x !== 'any'), p];
    return next.length ? next : ['any'];
  });
  useEffect(() => {
    const onEscape = e => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [saving, onClose]);
  // No iPhone evita deslocar o fundo da página enquanto o formulário está aberto.
  useEffect(() => {
    if (!window.matchMedia('(max-width: 639px)').matches) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);
  const save = async e => {
    e.preventDefault();
    if (saving) return;
    if (!from || !to || from > to || from < waitlistToday() || to > waitlistMaxDate()) {
      toast.error(pt ? 'Verifica o intervalo de datas (máximo 120 dias).' : 'Check your date range (max 120 days).'); return;
    }
    if ((Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z')) > 90 * 86400000) {
      toast.error(pt ? 'O intervalo não pode ultrapassar 91 dias.' : 'The range cannot exceed 91 days.'); return;
    }
    if (earliest && latest && earliest > latest) { toast.error(pt ? 'Hora inicial posterior à final.' : 'Start time is later than end time.'); return; }
    if (!whatsapp && !email) { toast.error(pt ? 'Escolhe pelo menos um canal de aviso.' : 'Choose at least one notification channel.'); return; }
    if (email && !user?.email) { toast.error(pt ? 'Adiciona um email ao teu perfil.' : 'Add an email to your profile.'); return; }
    if (whatsapp && !user?.phone?.trim()) { toast.error(pt ? 'Adiciona um número ao teu perfil.' : 'Add your phone number to your profile.'); return; }
    if (staffId !== 'any' && !compatibleStaff.some(p => p.staff_id === staffId)) {
      toast.error(pt ? 'Seleciona um profissional habilitado.' : 'Choose a qualified staff member.'); return;
    }
    setSaving(true);
    try {
      await api.patch(`/waitlist/${encodeURIComponent(entry.waitlist_id)}`, {
        service_ids: entry.service_ids,
        date_from: from,
        date_to: to,
        staff_id: staffId === 'any' ? null : staffId,
        periods,
        earliest_time: earliest || null,
        latest_time: latest || null,
        notes: notes.trim() || null,
        whatsapp_opt_in: whatsapp,
        email_opt_in: email,
      });
      toast.success(pt ? 'Preferências atualizadas!' : 'Preferences updated!');
      onSaved();
    } catch (error) {
      toast.error(waitlistError(error, pt ? 'Não foi possível atualizar o pedido.' : 'Could not update the request.'));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] flex items-end justify-center overflow-hidden bg-black/60 sm:items-center sm:p-4" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="waitlist-edit-title" className="flex h-[100dvh] max-h-[100dvh] min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-none bg-[#F9F8F6] shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-xl sm:rounded-2xl">
        <div className="z-10 flex shrink-0 items-start justify-between gap-3 border-b border-[#E6E2DA] bg-[#F9F8F6] px-4 py-3 sm:px-5 sm:py-4">
          <div><h3 id="waitlist-edit-title" className="font-heading text-xl text-[#1A1815]">{pt ? 'Editar lista de espera' : 'Edit waitlist request'}</h3><p className="mt-1 text-xs text-[#635E54]">{pt ? 'Os serviços mantêm-se. Para escolher outros serviços, cria um novo pedido.' : 'Services remain unchanged. To choose other services, create a new request.'}</p></div>
          <button aria-label={pt ? 'Fechar' : 'Close'} type="button" disabled={saving} onClick={onClose} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-[#E6E2DA]"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={save} className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain p-4 pb-0 [-webkit-overflow-scrolling:touch] sm:p-5" data-testid="waitlist-edit-form">
          <div className="rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm text-[#1A1815]">{entry.service_ids.map(id => services.find(s => s.service_id === id)?.[pt ? 'name_pt' : 'name_en'] || services.find(s => s.service_id === id)?.name_pt || id).join(' + ')}</div>
          <label className="block text-sm font-medium text-[#1A1815]">{pt ? 'Profissional' : 'Professional'}<select value={staffId} onChange={e => setStaffId(e.target.value)} className="mt-2 min-h-[44px] w-full min-w-0 rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm"><option value="any">{pt ? 'Qualquer profissional habilitado' : 'Any qualified professional'}</option>{compatibleStaff.map(p => <option key={p.staff_id} value={p.staff_id}>{p.name}</option>)}</select></label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium">{pt ? 'Data inicial' : 'Start date'}<input type="date" required min={waitlistToday()} max={waitlistMaxDate()} value={from} onChange={e => { setFrom(e.target.value); if (to < e.target.value) setTo(e.target.value); }} className="mt-2 min-h-[44px] w-full min-w-0 rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm" /></label>
            <label className="text-sm font-medium">{pt ? 'Data final' : 'End date'}<input type="date" required min={from || waitlistToday()} max={waitlistMaxDate()} value={to} onChange={e => setTo(e.target.value)} className="mt-2 min-h-[44px] w-full min-w-0 rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm" /></label>
          </div>
          <fieldset><legend className="mb-2 text-sm font-medium">{pt ? 'Quando podes vir?' : 'When are you available?'}</legend><div className="grid grid-cols-2 gap-2">{WAITLIST_PERIODS.map(p => <button key={p.key} type="button" aria-pressed={periods.includes(p.key)} onClick={() => togglePeriod(p.key)} className={`min-h-[44px] rounded-xl border px-3 py-2 text-left text-sm ${periods.includes(p.key) ? 'border-[#BFA05F] bg-[#BFA05F]/10 text-[#1A1815]' : 'border-[#E6E2DA] bg-white text-[#635E54]'}`}>{periods.includes(p.key) ? '✓ ' : ''}{pt ? p.pt : p.en}</button>)}</div></fieldset>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-[#635E54]">{pt ? 'A partir das' : 'Not before'}<input type="time" value={earliest} onChange={e => setEarliest(e.target.value)} className="mt-1 w-full rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm" /></label>
            <label className="text-xs text-[#635E54]">{pt ? 'Até às' : 'Not after'}<input type="time" value={latest} onChange={e => setLatest(e.target.value)} className="mt-1 w-full rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm" /></label>
          </div>
          <label className="block text-sm font-medium">{pt ? 'Observações' : 'Notes'}<textarea maxLength={500} rows={2} value={notes} onChange={e => setNotes(e.target.value)} className="mt-2 min-h-[44px] w-full min-w-0 rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm" /></label>
          <fieldset className="space-y-2 rounded-xl border border-[#E6E2DA] bg-white p-4"><legend className="px-1 text-sm font-medium">{pt ? 'Canais autorizados' : 'Notification channels'}</legend><label className="flex min-h-[44px] items-center gap-2 text-sm"><input type="checkbox" checked={email} onChange={e => setEmail(e.target.checked)} className="h-5 w-5 shrink-0 accent-[#BFA05F]" /> Email {user?.email ? `(${user.email})` : ''}</label><label className="flex min-h-[44px] items-center gap-2 text-sm"><input type="checkbox" checked={whatsapp} onChange={e => setWhatsapp(e.target.checked)} className="h-5 w-5 shrink-0 accent-[#BFA05F]" /> WhatsApp {user?.phone ? `(${user.phone})` : ''}</label></fieldset>
          <div className="sticky bottom-0 z-20 -mx-4 mt-6 flex flex-col gap-2 border-t border-[#E6E2DA] bg-[#F9F8F6] px-4 pt-3 shadow-[0_-6px_16px_rgba(0,0,0,0.04)] sm:-mx-5 sm:flex-row sm:px-5" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}><Button variant="outline" type="button" disabled={saving} onClick={onClose} className="min-h-[44px] w-full rounded-full sm:flex-1">{pt ? 'Voltar' : 'Back'}</Button><Button type="submit" disabled={saving} className="min-h-[48px] w-full rounded-full bg-[#1A1815] text-white hover:bg-[#2C2924] sm:flex-1">{saving ? (pt ? 'A guardar…' : 'Saving…') : (pt ? 'Guardar alterações' : 'Save changes')}</Button></div>
        </form>
      </div>
    </div>
  );
};
const WaitlistClientPanel = ({ language, user, offerId }) => {
  const pt = language === 'pt';
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editEntry, setEditEntry] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [config, setConfig] = useState(null);
  const [offers, setOffers] = useState([]);
  const [offersError, setOffersError] = useState('');
  const [offersRefreshing, setOffersRefreshing] = useState(false);
  const [offerBusy, setOfferBusy] = useState(null);
  const [clock, setClock] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // Carrega as ofertas da conta autenticada (não apenas os pedidos de espera).
  // Uma falha nesta chamada deixa de ser escondida como se não houvesse ofertas.
  const refreshOffers = async (showLoading = false) => {
    if (showLoading) setOffersRefreshing(true);
    try {
      const response = await api.get('/waitlist/offers/my');
      if (!Array.isArray(response.data)) {
        throw new Error('Resposta inválida ao consultar propostas de encaixe.');
      }
      setOffers(response.data);
      setOffersError('');
    } catch (err) {
      const message = waitlistError(
        err,
        pt ? 'Não foi possível consultar as propostas. Atualiza a página ou verifica o backend.' : 'Unable to load offers. Refresh or check the backend.'
      );
      console.warn('Erro ao carregar ofertas da lista de espera:', err);
      setOffersError(message);
      // Não apagar ofertas já carregadas em caso de erro transitório.
    } finally {
      if (showLoading) setOffersRefreshing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, servicesRes, staffRes, configRes] = await Promise.all([
        api.get('/waitlist/my'), api.get('/services'), api.get('/staff'), api.get('/waitlist/config'),
      ]);
      setEntries(Array.isArray(listRes.data) ? listRes.data : []);
      setServices(Array.isArray(servicesRes.data) ? servicesRes.data : []);
      setStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
      setConfig(configRes.data || null);
      await refreshOffers();
    } catch (err) {
      setError(waitlistError(err, pt ? 'Não foi possível carregar a lista de espera. Verifica se o backend está atualizado.' : 'Unable to load waitlist. Check the backend version.'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  // Quando uma colaboradora cria uma oferta, a cliente vê-a sem ter de recarregar.
  // A atualização só corre enquanto esta aba estiver montada e a página estiver visível.
  useEffect(() => {
    const updateIfVisible = () => {
      if (document.visibilityState === 'visible') refreshOffers();
    };
    const interval = window.setInterval(updateIfVisible, 15000);
    window.addEventListener('focus', updateIfVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', updateIfVisible);
    };
  }, []);
  const cancel = async entry => {
    const message = pt ? 'Queres mesmo cancelar este pedido? Isto não cancela nenhuma marcação existente.' : 'Cancel this waitlist request? This does not cancel any existing booking.';
    if (!window.confirm(message) || cancellingId) return;
    setCancellingId(entry.waitlist_id);
    try {
      await api.delete(`/waitlist/${encodeURIComponent(entry.waitlist_id)}`);
      setEntries(prev => prev.map(x => x.waitlist_id === entry.waitlist_id ? { ...x, status: 'cancelled', display_status: 'cancelled' } : x));
      toast.success(pt ? 'Pedido cancelado.' : 'Request cancelled.');
    } catch (err) { toast.error(waitlistError(err, pt ? 'Falha ao cancelar o pedido.' : 'Could not cancel the request.')); }
    finally { setCancellingId(null); }
  };
  const hideOffer = async (offer) => {
    if (offerBusy) return;
    if (!window.confirm(pt ? 'Ocultar esta proposta do teu Dashboard? A marcação e o histórico do estúdio não serão apagados.' : 'Hide this offer from your Dashboard? Your booking and studio records will remain.')) return;
    setOfferBusy(offer.offer_id);
    try {
      await api.delete(`/waitlist/offers/${encodeURIComponent(offer.offer_id)}`);
      setOffers(previous => previous.filter(item => item.offer_id !== offer.offer_id));
      toast.success(pt ? 'Proposta removida da tua vista.' : 'Offer hidden from your view.');
    } catch (err) {
      toast.error(waitlistError(err, pt ? 'Não foi possível ocultar a proposta.' : 'Unable to hide the offer.'));
    } finally { setOfferBusy(null); }
  };
  const respondOffer = async (offer, choice) => {
    if (offerBusy) return;
    if (choice === 'accept' && !window.confirm(pt
      ? 'Confirmar esta marcação com pagamento no estabelecimento? A reserva ficará registada imediatamente.'
      : 'Confirm this booking with payment at the studio? The appointment will be registered immediately.')) return;
    setOfferBusy(offer.offer_id);
    try {
      const url = `/waitlist/offers/${encodeURIComponent(offer.offer_id)}/${choice}`;
      await api.post(url, choice === 'accept' ? { payment_method: 'in_store' } : {});
      toast.success(choice === 'accept' ? (pt ? 'Encaixe confirmado! Consulta as tuas marcações.' : 'Booking confirmed! Check your appointments.')
        : (pt ? 'Proposta recusada. Continuas na lista de espera.' : 'Offer declined. Your request remains active.'));
      await load();
      if (choice === 'accept') window.location.assign('/dashboard/bookings');
    } catch (err) {
      toast.error(waitlistError(err, pt ? 'Não foi possível responder à oferta.' : 'Could not respond to the offer.'));
      await load();
    } finally { setOfferBusy(null); }
  };
  // Ofertas pendentes primeiro, ofertas antigas depois.
  const orderedOffers = [...offers].sort((a, b) => {
    const activeA = a.status === 'pending' && (toUTCDate(a.expires_at)?.getTime() || 0) > clock ? 1 : 0;
    const activeB = b.status === 'pending' && (toUTCDate(b.expires_at)?.getTime() || 0) > clock ? 1 : 0;
    return activeB - activeA || String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });
  const activeEntries = entries.filter(x => (x.display_status || x.status) === 'waiting');
  const pastEntries = entries.filter(x => (x.display_status || x.status) !== 'waiting');
  const serviceName = id => {
    const service = services.find(s => s.service_id === id);
    return (pt ? service?.name_pt : service?.name_en || service?.name_pt) || id;
  };
  const personName = id => !id ? (pt ? 'Qualquer profissional habilitada' : 'Any qualified professional') : staff.find(s => s.staff_id === id)?.name || (pt ? 'Profissional selecionada' : 'Selected professional');
  const periodName = x => (x.periods || ['any']).map(k => WAITLIST_PERIODS.find(p => p.key === k)?.[pt ? 'pt' : 'en'] || k).join(', ');
  return (
    <div className="min-w-0 space-y-5 pb-6" data-testid="waitlist-my-panel">
      {offerId && !offers.some(o => o.offer_id === offerId) && !loading && (
        <div className="rounded-2xl border border-[#BFA05F] bg-[#BFA05F]/10 p-5" data-testid="waitlist-offer-link">
          <div className="mb-2 flex items-center gap-2"><BellRing className="h-5 w-5 text-[#BFA05F]"/><h3 className="font-heading text-lg">{pt ? 'Oferta de encaixe' : 'Booking offer'}</h3></div>
          <p className="text-sm text-[#635E54]">{pt ? 'Esta oferta não está disponível nesta conta ou ainda não foi ativada. Nenhuma marcação foi confirmada.' : 'This offer is not available for this account or has not been activated. No booking was confirmed.'}</p>
        </div>
      )}
      <section className="space-y-3" data-testid="waitlist-offers-panel">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h3 className="font-heading text-xl text-[#1A1815]">{pt ? 'Propostas de encaixe' : 'Appointment offers'}</h3>
            <p className="mt-1 text-xs text-[#635E54]">{pt ? 'As propostas criadas pela equipa aparecem aqui. Tens 20 minutos para responder.' : 'Offers made by our team appear here. You have 20 minutes to respond.'}</p>
          </div>
          <Button type="button" size="sm" variant="outline" disabled={offersRefreshing || !!offerBusy} onClick={() => refreshOffers(true)} className="min-h-[44px] w-full rounded-full sm:w-auto">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${offersRefreshing ? 'animate-spin' : ''}`} />
            {offersRefreshing ? (pt ? 'A atualizar…' : 'Refreshing…') : (pt ? 'Atualizar propostas' : 'Refresh offers')}
          </Button>
        </div>
        {offersError && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {offersError}
          </div>
        )}
        {!loading && !offersError && orderedOffers.length === 0 && (
          <div className="rounded-2xl border border-[#E6E2DA] bg-white px-5 py-6">
            <p className="font-medium text-[#1A1815]">{pt ? 'Ainda não tens propostas para aceitar.' : 'You have no offers to accept yet.'}</p>
            <p className="mt-1 text-sm text-[#635E54]">{pt ? 'O teu pedido já está na lista. Quando uma colaboradora selecionar uma vaga compatível e criar uma oferta, aparecem aqui os botões Aceitar e Recusar.' : 'Your request is on the waitlist. Once a team member offers you a matching slot, Accept and Decline buttons will appear here.'}</p>
          </div>
        )}
        {orderedOffers.map(offer => {
            const expires = toUTCDate(offer.expires_at)?.getTime() || 0;
            const timeLeft = Math.max(0, Math.ceil((expires - clock) / 1000));
            const valid = offer.status === 'pending' && timeLeft > 0;
            const safeToConfirm = valid && config?.manual_offers_enabled;
            const label = valid ? (pt ? 'À espera da tua resposta' : 'Awaiting your response')
              : ({converted:pt?'Confirmado':'Confirmed',declined:pt?'Recusado':'Declined',expired:pt?'Expirado':'Expired',pending:pt?'Prazo terminado':'Expired'}[offer.status] || offer.status);
            const highlighted = offer.offer_id === offerId;
            return <article key={offer.offer_id} className={`min-w-0 scroll-mt-28 rounded-2xl border p-4 sm:p-5 ${highlighted ? 'border-[#BFA05F] bg-[#BFA05F]/5' : 'border-[#E6E2DA] bg-white'}`} data-testid={`waitlist-offer-${offer.offer_id}`}>
              <div className="mb-3 flex min-w-0 flex-wrap items-start justify-between gap-2"><h4 className="font-heading text-lg font-medium text-[#1A1815]">{pt ? 'Temos uma vaga para ti!' : 'A slot is available!'}</h4><span className="rounded-full bg-[#F2F0EB] px-3 py-1 text-xs text-[#635E54]">{label}</span></div>
              <p className="mb-2 break-words text-sm text-[#635E54]">{offer.service_names?.join(' + ') || (pt ? 'Serviço escolhido' : 'Selected service')}</p>
              <p className="mb-1 text-sm"><Calendar className="mr-2 inline h-4 w-4 text-[#BFA05F]"/>{formatDateTimeLisbon(offer.start_time)}</p>
              <p className="mb-1 text-sm"><User className="mr-2 inline h-4 w-4 text-[#BFA05F]"/>{offer.staff_name || 'Lou’s Way'}</p>
              {Number.isFinite(Number(offer.total_price)) && <p className="mb-2 text-sm font-medium">{pt ? 'Valor dos serviços' : 'Services total'}: {new Intl.NumberFormat('pt-PT',{style:'currency',currency:'EUR'}).format(Number(offer.total_price))}</p>}
              {valid && <p className="mt-3 text-sm text-[#635E54]"><Clock className="mr-1.5 inline h-4 w-4"/>{pt ? 'Tempo restante' : 'Time remaining'}: <strong>{`${Math.floor(timeLeft / 60).toString().padStart(2,'0')}:${(timeLeft % 60).toString().padStart(2,'0')}`}</strong></p>}
              {valid && <p className="mt-2 text-xs text-[#635E54]">{pt ? 'Aceitar cria a marcação com pagamento no estabelecimento. O horário só fica confirmado após validação pelo servidor.' : 'Accepting creates a booking payable at the studio, subject to server validation.'}</p>}
              {valid && <div className="mt-4 grid grid-cols-1 gap-2 min-[390px]:grid-cols-2">
                <Button variant="outline" className="min-h-[48px] w-full rounded-full border-[#D1CCC4]" disabled={!!offerBusy} onClick={() => respondOffer(offer, 'decline')}><X className="mr-1.5 h-4 w-4"/>{pt ? 'Recusar' : 'Decline'}</Button>
                <Button className="min-h-[48px] w-full rounded-full bg-[#1A1815] text-white hover:bg-[#2C2924]" disabled={!safeToConfirm || !!offerBusy} onClick={() => respondOffer(offer, 'accept')}><ShieldCheck className="mr-1.5 h-4 w-4"/>{offerBusy === offer.offer_id ? (pt ? 'A processar…' : 'Processing…') : (pt ? 'Aceitar' : 'Accept')}</Button>
              </div>}
              {!valid && offer.status !== 'pending' && offer.status !== 'converting' && <div className="mt-4 border-t border-[#E6E2DA] pt-3 flex justify-end"><Button type="button" size="sm" variant="outline" disabled={!!offerBusy} className="min-h-[44px] w-full rounded-full border-[#E6E2DA] text-[#635E54] sm:w-auto" onClick={() => hideOffer(offer)}><X className="mr-1.5 h-4 w-4"/>{pt ? 'Remover da minha vista' : 'Hide this offer'}</Button></div>}
              {valid && !safeToConfirm && <p className="mt-2 text-xs text-amber-800">{pt ? 'A aceitação ainda não está ativada neste ambiente.' : 'Offer acceptance is not enabled in this environment.'}</p>}
            </article>;
          })}
      </section>
      <div className="flex min-w-0 flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center">
        <div><h2 className="font-heading text-xl font-medium text-[#1A1815]">{pt ? 'Os meus pedidos de espera' : 'My waitlist requests'}</h2><p className="text-sm text-[#635E54]">{pt ? 'Consulta e ajusta as tuas preferências, sem alterar as marcações existentes.' : 'Manage your preferences without changing existing bookings.'}</p></div>
        <Button onClick={() => navigate('/booking')} className="min-h-[44px] w-full rounded-full bg-[#1A1815] text-white hover:bg-[#2C2924] sm:w-auto"><CalendarClock className="mr-2 h-4 w-4" />{pt ? 'Procurar serviços' : 'Find services'}</Button>
      </div>
      {config && !config.manual_offers_enabled && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"><ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" /><span>{pt ? 'Fase de testes: os pedidos ficam guardados, mas o envio automático de ofertas e a confirmação de encaixes ainda estão desativados.' : 'Testing phase: requests are stored, but automatic offers and confirmations are still disabled.'}</span></div>
      )}
      {loading ? <p className="rounded-2xl border border-[#E6E2DA] bg-white p-6 text-sm text-[#635E54]">{pt ? 'A carregar lista de espera…' : 'Loading waitlist…'}</p> : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5"><p className="text-sm text-red-800">{error}</p><Button type="button" variant="outline" onClick={load} className="mt-3 rounded-full"><RefreshCw className="mr-2 h-4 w-4" />{pt ? 'Tentar novamente' : 'Retry'}</Button></div>
      ) : entries.length === 0 ? (
        <div className="rounded-2xl border border-[#E6E2DA] bg-white p-8 text-center"><BellRing className="mx-auto mb-3 h-10 w-10 text-[#BFA05F]" /><h3 className="font-heading text-lg text-[#1A1815]">{pt ? 'Ainda não tens pedidos' : 'No requests yet'}</h3><p className="mt-2 text-sm text-[#635E54]">{pt ? 'Se os horários disponíveis não te servirem, podes pedir para ficar em lista de espera ao escolher os serviços.' : 'If available slots do not suit you, join the waitlist when choosing your services.'}</p></div>
      ) : (
        <div className="space-y-5">
          {[{ key: 'active', title: pt ? 'Pedidos ativos' : 'Active requests', list: activeEntries }, { key: 'history', title: pt ? 'Pedidos anteriores' : 'Past requests', list: pastEntries }].filter(group => group.list.length).map(group => (
            <section key={group.key} className="space-y-3"><h3 className="text-sm font-semibold text-[#635E54]">{group.title} ({group.list.length})</h3>
              {group.list.map(entry => {
                const status = entry.display_status || entry.status || 'waiting';
                const editable = status === 'waiting';
                return <article key={entry.waitlist_id} className="min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-4 sm:p-5" data-testid={`waitlist-request-${entry.waitlist_id}`}>
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-2"><h4 className="font-medium text-[#1A1815]">{entry.service_ids?.map(serviceName).join(' + ') || (pt ? 'Serviço indisponível' : 'Service unavailable')}</h4><span className={`rounded-full border px-3 py-1 text-xs ${waitlistStatusStyle[status] || waitlistStatusStyle.expired}`}>{(waitlistStatusLabel[pt ? 'pt' : 'en'])[status] || status}</span></div>
                  <div className="space-y-1.5 text-sm text-[#635E54]"><p><Calendar className="mr-2 inline h-4 w-4 text-[#BFA05F]" />{waitlistDatePT(entry.date_from)} — {waitlistDatePT(entry.date_to)}</p><p><Clock className="mr-2 inline h-4 w-4 text-[#BFA05F]" />{periodName(entry)}{entry.earliest_time ? ` · ${pt ? 'a partir das' : 'from'} ${entry.earliest_time}` : ''}{entry.latest_time ? ` · ${pt ? 'até às' : 'until'} ${entry.latest_time}` : ''}</p><p><User className="mr-2 inline h-4 w-4 text-[#BFA05F]" />{personName(entry.staff_id)}</p>{entry.notes && <p className="break-words text-xs italic">{entry.notes}</p>}<p className="text-xs">{pt ? 'Avisos autorizados:' : 'Notifications allowed:'} {[entry.email_opt_in && 'Email', entry.whatsapp_opt_in && 'WhatsApp'].filter(Boolean).join(' + ') || '—'}</p></div>
                  {(editable || entry.status === 'waiting') && <div className="mt-4 flex flex-col gap-2 border-t border-[#E6E2DA] pt-4 sm:flex-row sm:flex-wrap">{editable && <Button variant="outline" size="sm" onClick={() => setEditEntry(entry)} className="min-h-[44px] w-full rounded-full sm:w-auto"><Pencil className="mr-1.5 h-4 w-4" />{pt ? 'Editar preferências' : 'Edit preferences'}</Button>}{entry.status === 'waiting' && <Button variant="outline" size="sm" disabled={cancellingId === entry.waitlist_id} onClick={() => cancel(entry)} className="min-h-[44px] w-full rounded-full border-red-200 text-red-700 hover:bg-red-50 sm:w-auto"><X className="mr-1.5 h-4 w-4" />{cancellingId === entry.waitlist_id ? (pt ? 'A cancelar…' : 'Cancelling…') : (pt ? 'Cancelar pedido' : 'Cancel request')}</Button>}</div>}
                </article>;
              })}
            </section>
          ))}
        </div>
      )}
      {editEntry && <WaitlistEditModal key={editEntry.waitlist_id} entry={editEntry} services={services} staff={staff} user={user} language={language} onClose={() => setEditEntry(null)} onSaved={() => { setEditEntry(null); load(); }} />}
    </div>
  );
};
const DashboardPage = () => {
  // Adicionado loading do useAuth para controle de redirecionamento
  const { user, updateProfile, loading: authLoading } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [frequentServices, setFrequentServices] = useState([]);
  const [loyalty, setLoyalty] = useState({ points: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const offerId = new URLSearchParams(location.search).get('offer');
  const [activeTab, setActiveTab] = useState(() => WAITLIST_UI_ENABLED && offerId ? 'waitlist' : 'bookings');
  useEffect(() => { if (WAITLIST_UI_ENABLED && offerId) setActiveTab('waitlist'); }, [offerId]);
  // NOVO: Verificação de perfil completo (Telefone)
  useEffect(() => {
    if (!authLoading && user && !user.phone) {
      navigate('/profile');
    }
  }, [user, authLoading, navigate]);
  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, []);
  const fetchData = async () => {
    try {
      const [bookingsRes, frequentRes, loyaltyRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/bookings/frequent'),
        api.get('/loyalty'),
      ]);
      setBookings(bookingsRes.data);
      setFrequentServices(frequentRes.data);
      setLoyalty(loyaltyRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  const checkPaymentStatus = async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const status = params.get('status');
    if (sessionId && status === 'success') {
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          toast.success(language === 'pt' ? 'Pagamento realizado com sucesso!' : 'Payment successful!');
          fetchData();
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
      // Clean URL
      navigate('/dashboard/bookings', { replace: true });
    }
  };
  const handlePayOnline = async (bookingId) => {
    try {
      const response = await api.post('/payments/create-checkout', {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(language === 'pt' ? 'Erro ao processar pagamento' : 'Payment error');
    }
  };
  const handleCancelBooking = async (bookingId) => {
    try {
      await api.put(`/bookings/${bookingId}/cancel`, {});
      toast.success(language === 'pt' ? 'Marcação cancelada' : 'Booking cancelled');
      fetchData();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(language === 'pt' ? 'Erro ao cancelar' : 'Error cancelling');
    }
  };
  // CORREÇÃO: Usar toUTCDate para garantir comparação consistente de timestamps
  const now = new Date();
  const upcomingBookings = bookings.filter((b) => {
    const bDate = toUTCDate(b.start_time);
    return bDate && bDate >= now && b.status !== 'cancelled';
  });
  const pastBookings = bookings.filter((b) => {
    const bDate = toUTCDate(b.start_time);
    return bDate && (bDate < now || b.status === 'cancelled');
  });
  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };
  // CORREÇÃO: Usar formatDateTimeLisbon para exibir sempre no timezone de Lisboa
  const formatDate = (dateStr) => {
    return formatDateTimeLisbon(dateStr);
  };
  // Modificado para impedir renderização se o usuário não tiver telefone
  if (loading || authLoading || (user && !user.phone)) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-[#BFA05F]">{t('common.loading')}</div>
      </div>
    );
  }
  return (
    <div className="min-h-[100svh] min-w-0 overflow-x-clip bg-[#F9F8F6] pt-24 pb-16" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 60px)" }} data-testid="dashboard-page">
      <div className="mx-auto w-full min-w-0 max-w-7xl px-4 sm:px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="mb-6 animate-fade-in-up sm:mb-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-heading break-words text-2xl font-medium text-[#1A1815] sm:text-3xl md:text-4xl">
                {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
              </h1>
              <p className="mt-1 break-all text-sm text-[#635E54] sm:text-base">{user?.email}</p>
            </div>
            <Button
              onClick={() => navigate('/booking')}
              className="min-h-[44px] w-full rounded-full bg-[#1A1815] px-6 text-white hover:bg-[#2C2924] sm:w-auto"
              data-testid="new-booking-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {t('nav.book')}
            </Button>
          </div>
        </div>
        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:mb-10 sm:gap-6 md:grid-cols-3">
          <div
            className="min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-3 animate-fade-in-up sm:p-6"
            data-testid="stat-bookings"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#F2F0EB] flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#BFA05F]" />
              </div>
              <div>
                <p className="text-sm text-[#635E54]">{t('dashboard.upcoming')}</p>
                <p className="text-2xl font-semibold text-[#1A1815]">{upcomingBookings.length}</p>
              </div>
            </div>
          </div>
          <div
            className="min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-3 animate-fade-in-up animation-delay-100 sm:p-6"
            data-testid="stat-points"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#BFA05F]/10 flex items-center justify-center">
                <Gift className="w-6 h-6 text-[#BFA05F]" />
              </div>
              <div>
                <p className="text-sm text-[#635E54]">{t('dashboard.loyalty')}</p>
                <p className="text-2xl font-semibold text-[#BFA05F]">
                  {loyalty.points} {t('dashboard.points')}
                </p>
              </div>
            </div>
          </div>
          <div
            className="col-span-2 min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-3 animate-fade-in-up animation-delay-200 sm:p-6 md:col-span-1"
            data-testid="stat-history"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#F2F0EB] flex items-center justify-center">
                <History className="w-6 h-6 text-[#635E54]" />
              </div>
              <div>
                <p className="text-sm text-[#635E54]">{t('dashboard.history')}</p>
                <p className="text-2xl font-semibold text-[#1A1815]">{bookings.length}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-8">
          {/* Bookings */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-5 grid h-auto min-h-[56px] w-full min-w-0 grid-cols-3 gap-1 rounded-2xl border border-[#E6E2DA] bg-white p-1 sm:mb-6 sm:rounded-full">
                <TabsTrigger
                  value="bookings"
                  className="min-h-[48px] min-w-0 whitespace-normal break-words rounded-xl px-1 py-2 text-center text-[11px] leading-tight data-[state=active]:bg-[#1A1815] data-[state=active]:text-white sm:rounded-full sm:px-3 sm:text-sm"
                  data-testid="tab-bookings"
                >
                  {t('dashboard.my_bookings')}
                </TabsTrigger>
                <TabsTrigger
                  value="history"
                  className="min-h-[48px] min-w-0 whitespace-normal break-words rounded-xl px-1 py-2 text-center text-[11px] leading-tight data-[state=active]:bg-[#1A1815] data-[state=active]:text-white sm:rounded-full sm:px-3 sm:text-sm"
                  data-testid="tab-history"
                >
                  {t('dashboard.history')}
                </TabsTrigger>
                {WAITLIST_UI_ENABLED && <TabsTrigger value="waitlist" className="min-h-[48px] min-w-0 whitespace-normal break-words rounded-xl px-1 py-2 text-center text-[11px] leading-tight data-[state=active]:bg-[#1A1815] data-[state=active]:text-white sm:rounded-full sm:px-3 sm:text-sm" data-testid="tab-waitlist"><BellRing className="mr-1 hidden h-4 w-4 sm:inline" />{language === 'pt' ? 'Lista de Espera' : 'Waitlist'}</TabsTrigger>}
              </TabsList>
              <TabsContent value="bookings" className="space-y-4">
                {upcomingBookings.length === 0 ? (
                  <div className="bg-white rounded-2xl p-10 text-center border border-[#E6E2DA]">
                    <Calendar className="w-12 h-12 text-[#D1CCC4] mx-auto mb-4" />
                    <p className="text-[#635E54]">{t('dashboard.no_bookings')}</p>
                    <Button
                      onClick={() => navigate('/booking')}
                      className="mt-4 bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full"
                      data-testid="book-first-btn"
                    >
                      {t('dashboard.book_first')}
                    </Button>
                  </div>
                ) : (
                  upcomingBookings.map((booking) => (
                    <div
                      key={booking.booking_id}
                      className="min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-4 transition-colors hover:border-[#BFA05F]/30 sm:p-6"
                      data-testid={`booking-${booking.booking_id}`}
                    >
                      <div className="flex min-w-0 flex-col justify-between gap-4 md:flex-row md:items-center">
                        <div className="flex min-w-0 gap-3 sm:gap-4">
                          <img
                            src={booking.service?.images?.[0] || 'https\://images.pexels.com/photos/5659012/pexels-photo-5659012.jpeg'}
                            alt=""
                            className="h-16 w-16 shrink-0 rounded-xl object-cover sm:h-20 sm:w-20"
                          />
                          <div className="min-w-0 flex-1">
                            <h3 className="break-words font-medium text-[#1A1815]">
                              {language === 'pt' ? booking.service?.name_pt : booking.service?.name_en}
                            </h3>
                            <p className="text-sm text-[#635E54] flex items-center gap-1 mt-1">
                              <Clock className="w-4 h-4" />
                              {formatDate(booking.start_time)}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className={getStatusBadge(booking.status)}>
                                {t(`dashboard.status.${booking.status}`)}
                              </Badge>
                              {booking.payment_status === 'pending' && (
                                <Badge className="bg-orange-100 text-orange-800">
                                  {t('dashboard.payment.pending')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {booking.payment_status === 'pending' && booking.payment_method === 'online' && (
                            <Button
                              onClick={() => handlePayOnline(booking.booking_id)}
                              className="bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full"
                              size="sm"
                              data-testid={`pay-btn-${booking.booking_id}`}
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              {t('dashboard.payment.pay_now')}
                            </Button>
                          )}
                          {(booking.status === 'pending' || booking.status === 'confirmed') && (
                            <Button
                              onClick={() => {
                                if (window.confirm(language === 'pt' ? 'Tem a certeza que deseja cancelar esta marcação?' : 'Are you sure you want to cancel?')) {
                                  handleCancelBooking(booking.booking_id);
                                }
                              }}
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50 rounded-full"
                              data-testid={`cancel-btn-${booking.booking_id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              {t('dashboard.cancel')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>
              <TabsContent value="history" className="space-y-4">
                {pastBookings.length === 0 ? (
                  <div className="bg-white rounded-2xl p-10 text-center border border-[#E6E2DA]">
                    <History className="w-12 h-12 text-[#D1CCC4] mx-auto mb-4" />
                    <p className="text-[#635E54]">{t('dashboard.no_bookings')}</p>
                  </div>
                ) : (
                  pastBookings.map((booking) => (
                    <div
                      key={booking.booking_id}
                      className="bg-white rounded-2xl p-6 border border-[#E6E2DA] opacity-75"
                      data-testid={`history-${booking.booking_id}`}
                    >
                      <div className="flex gap-4">
                        <img
                          src={booking.service?.images?.[0] || 'https\://images.pexels.com/photos/5659012/pexels-photo-5659012.jpeg'}
                          alt=""
                          className="w-16 h-16 rounded-xl object-cover grayscale"
                        />
                        <div>
                          <h3 className="font-medium text-[#1A1815]">
                            {language === 'pt' ? booking.service?.name_pt : booking.service?.name_en}
                          </h3>
                          <p className="text-sm text-[#635E54]">{formatDate(booking.start_time)}</p>
                          <Badge className={getStatusBadge(booking.status)}>
                            {t(`dashboard.status.${booking.status}`)}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </TabsContent>
              {WAITLIST_UI_ENABLED && <TabsContent value="waitlist" className="space-y-4">{activeTab === 'waitlist' && <WaitlistClientPanel language={language} user={user} offerId={offerId} />}</TabsContent>}
            </Tabs>
          </div>
          {/* Sidebar */}
          <div className="space-y-6">
            <div
              className="min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-3 animate-fade-in-up sm:p-6"
              data-testid="frequent-services"
            >
              <h3 className="font-heading text-lg font-medium text-[#1A1815] mb-4">
                {t('dashboard.frequent')}
              </h3>
              {frequentServices.length === 0 ? (
                <p className="text-sm text-[#635E54]">{t('dashboard.no_bookings')}</p>
              ) : (
                <div className="space-y-3">
                  {frequentServices.slice(0, 3).map((service) => (
                    <button
                      key={service.service_id}
                      onClick={() => navigate(`/booking?service=${service.service_id}`)}
                      className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-[#F9F8F6] transition-colors text-left"
                      data-testid={`frequent-${service.service_id}`}
                    >
                      <div>
                        <p className="font-medium text-[#1A1815] text-sm">
                          {language === 'pt' ? service.name_pt : service.name_en}
                        </p>
                        <p className="text-xs text-[#635E54]">
                          {service.booking_count}x {language === 'pt' ? 'reservado' : 'booked'}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#BFA05F]" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div
              className="bg-gradient-to-br from-[#1A1815] to-[#2C2924] rounded-2xl p-6 text-white animate-fade-in-up animation-delay-100"
              data-testid="loyalty-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <Gift className="w-6 h-6 text-[#BFA05F]" />
                <h3 className="font-heading text-lg font-medium">{t('dashboard.loyalty')}</h3>
              </div>
              <p className="text-3xl font-semibold text-[#BFA05F]">
                {loyalty.points} <span className="text-sm font-normal text-white/70">{t('dashboard.points')}</span>
              </p>
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-xs text-white/60">
                  {language === 'pt'
                    ? 'Ganhe pontos por cada marcação!'
                    : 'Earn points per booking!'}
                </p>
              </div>
            </div>
            <div
              className="bg-white rounded-2xl p-6 border border-[#E6E2DA] animate-fade-in-up animation-delay-200"
              data-testid="profile-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <User className="w-5 h-5 text-[#BFA05F]" />
                <h3 className="font-heading text-lg font-medium text-[#1A1815]">
                  {t('dashboard.profile')}
                </h3>
              </div>
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#BFA05F]"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#BFA05F] flex items-center justify-center text-white text-xl">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
                <div>
                  <p className="font-medium text-[#1A1815]">{user?.name}</p>
                  <p className="text-sm text-[#635E54]">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default DashboardPage;
